import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { TitleText, BodyText } from '@/components/ui';
import { AvatarPhoto } from '@/components/ui/AvatarPhoto';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/utils/cn';
import { apiService } from '@/services';
import { getLoggerWithPrefix } from '@/services/shared/LoggerFactory';
import { PaymentGuidance } from '@/components/payment-guidance';
import type { MatchedDietitian } from '@/components/self-referral/types';

interface DirectConfirmationStepProps {
  dietitian: MatchedDietitian;
  isAuthenticated: boolean;
  onConfirm: () => void;
  isLoading: boolean;
}

export const DirectConfirmationStep: React.FC<DirectConfirmationStepProps> = ({
  dietitian,
  isAuthenticated,
  onConfirm,
  isLoading
}) => {
  const logger = getLoggerWithPrefix('DirectConfirmationStep');
  const { isDark } = useTheme();
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [firstName, ...restName] = dietitian.name.trim().split(' ');
  const dietitianName = `${firstName} ${restName.join(' ')}`.trim();

  // Fetch the dietitian photo using the shared photo service to match selection UI
  useEffect(() => {
    let isMounted = true;

    const fetchPhoto = async () => {
      const initialPhoto = (dietitian as { photo?: string | null }).photo;
      if (initialPhoto) {
        setPhotoUrl(initialPhoto);
        return;
      }
      if (!dietitian.dietician_id) return;

      try {
        const response = await apiService.photo.getPhotoUrls({ user_ids: [dietitian.dietician_id] });
        if (!isMounted) return;
        setPhotoUrl(response?.photo_urls?.[dietitian.dietician_id] ?? null);
      } catch (error) {
        console.warn('DirectConfirmationStep: Failed to fetch dietitian photo', error);
        if (isMounted) {
          setPhotoUrl(null);
        }
      }
    };

    fetchPhoto();

    return () => {
      isMounted = false;
    };
  }, [dietitian]);

  return (
    <div className="animate-fade-in space-y-8 text-center">
      {/* Title */}
      <div className="space-y-2">
        <TitleText level={1}>
          Book with {dietitianName}
        </TitleText>
        <BodyText variant="secondary" className={cn(
          'text-base',
          isDark ? 'text-secondary-300' : 'text-secondary-700'
        )}>
          Confirm your booking details below.
        </BodyText>
      </div>

      {/* Dietitian details (non-selectable, aligned with selection list item style) */}
      <div className={cn(
        'w-full flex items-start gap-4 justify-center',
        'max-w-2xl mx-auto'
      )}>
        <div className="flex-shrink-0">
          <AvatarPhoto
            src={photoUrl}
            firstName={firstName}
            lastName={restName.join(' ')}
            alt={`${dietitianName} profile photo`}
            size="lg"
          />
        </div>

        <div className="flex-1 space-y-2 text-left">
          <TitleText level={4} className={cn(
            'leading-tight',
            isDark ? 'text-white' : 'text-gray-900'
          )}>
            {dietitianName}
          </TitleText>

          {dietitian.specializations && dietitian.specializations.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {dietitian.specializations.map((spec, idx) => (
                <span
                  key={idx}
                  className={cn(
                    'px-2 py-1 rounded-full text-xs font-medium',
                    isDark ? 'bg-primary-900 text-primary-200' : 'bg-primary-100 text-primary-800'
                  )}
                >
                  {spec}
                </span>
              ))}
            </div>
          )}

          <BodyText variant="secondary" className={cn(
            'max-w-2xl',
            isDark ? 'text-secondary-300' : 'text-secondary-700'
          )}>
            {isAuthenticated
              ? `You are about to book an appointment with ${dietitianName}.`
              : `To finalize your booking with ${dietitianName}, please create an account.`
            }
          </BodyText>
        </div>
      </div>

      {/* Fee guidance (design §4.6, §4.13.2) */}
      <div className="mx-auto max-w-2xl">
        <PaymentGuidance guidance={dietitian.payment_guidance ?? null} />
      </div>

      {/* Actions */}
      <div className="space-y-3">
        <Button
          onClick={onConfirm}
          disabled={isLoading}
          size="lg"
          className="w-full sm:w-auto min-w-[240px]"
        >
          {isLoading ? "Processing..." : (isAuthenticated ? "Continue to Dashboard" : "Continue to Registration")}
        </Button>

        {!isAuthenticated && (
          <p className={cn("text-sm", isDark ? "text-secondary-400" : "text-secondary-600")}>
            Already have an account? The registration page will allow you to sign in.
          </p>
        )}
      </div>

      {/* Support */}
      <div className="max-w-2xl mx-auto">
        <p className={cn(
          "text-sm mb-4",
          isDark ? "text-secondary-400" : "text-secondary-600"
        )}>
          If you have any questions about your booking or need help getting started, please contact our support team.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            variant="ghost"
            size="md"
            onClick={() => logger.log('Contact support clicked')}
            className="sm:w-auto"
          >
            Contact Support
          </Button>
        </div>
      </div>
    </div>
  );
};
